import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import type { CounterKey, SessionFeatures } from './types';

export type { CounterKey, SessionFeatures };

type SessionFeaturesActions = {
  _increment: (counter: CounterKey, key: string) => void;
  _set: (key: 'channel' | 'userAgent' | 'isCheckout', value: string | boolean) => void;
  _syncSession: (newSessionId: string) => void;
  _reset: () => void;
};

const INITIAL_STATE: SessionFeatures = {
  counters: { n_partners_located: 0, n_started: 0, n_debts_clicked: 0, n_debts_viewed: 0 },
  seen: { n_partners_located: [], n_started: [], n_debts_clicked: [], n_debts_viewed: [] },
  channel: null,
  userAgent: null,
  isCheckout: false,
  sessionId: null,
};

export const useSessionFeaturesStore = create<SessionFeatures & SessionFeaturesActions>()(
  persist(
    (set, get) => ({
      ...INITIAL_STATE,
      _increment: (counter, key) => {
        const state = get();
        if (state.seen[counter].includes(key)) return;
        set({
          counters: { ...state.counters, [counter]: state.counters[counter] + 1 },
          seen: { ...state.seen, [counter]: [...state.seen[counter], key] },
        });
      },
      _set: (key, value) => set({ [key]: value } as Partial<SessionFeatures>),
      _syncSession: (newSessionId) => {
        const { sessionId } = get();
        if (sessionId !== newSessionId) {
          set({ ...INITIAL_STATE, sessionId: newSessionId });
        }
      },
      _reset: () => set(INITIAL_STATE),
    }),
    {
      name: 'session-features',
      storage: createJSONStorage(() => sessionStorage),
      partialize: ({ _increment, _set, _syncSession, _reset, ...state }) => state,
    }
  )
);

export const incrementEvent = (counter: CounterKey, partnerSystemKey: string, sessionId?: string): void => {
  if (!partnerSystemKey) return;
  const store = useSessionFeaturesStore.getState();
  if (sessionId) store._syncSession(sessionId);
  store._increment(counter, partnerSystemKey);
};

export const setAttribute = (key: 'channel' | 'userAgent' | 'isCheckout', value: string | boolean): void => {
  useSessionFeaturesStore.getState()._set(key, value);
};

export const getSessionFeatures = (): {
  counters: Record<CounterKey, number>;
  channel: string | null;
  userAgent: string | null;
  isCheckout: boolean;
  sessionId: string | null;
} => {
  const { counters, channel, userAgent, isCheckout, sessionId } = useSessionFeaturesStore.getState();
  return { counters, channel, userAgent, isCheckout, sessionId };
};

export const syncSession = (sessionId: string): void => {
  useSessionFeaturesStore.getState()._syncSession(sessionId);
};

export const reset = (): void => {
  useSessionFeaturesStore.getState()._reset();
};

export const SESSION_FEATURES_HEADER = 'x-session-features';

export const encodeSessionFeatures = (): string => {
  const json = JSON.stringify(getSessionFeatures());
  const utf8Safe = encodeURIComponent(json).replace(/%([0-9A-F]{2})/g, (_, hex) => String.fromCharCode(parseInt(hex, 16)));
  return btoa(utf8Safe);
};

export { collectSessionAttributes } from './collectSessionAttributes';
